import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { ISelectDatepicker } from 'react-select-datepicker'
import {
  getDaysObject,
  getMonthsObject,
  getYearsObject,
} from 'react-select-datepicker/dist/utils/helpers'
import clsx from 'clsx'
import { Option, SelectRenderer } from './SelectRenderer'
import styles from './SelectDatepicker.module.scss'

interface ISelectDatepickerCustom extends ISelectDatepicker {
  white?: boolean,
}

function SelectDatepicker({
  id,
  className,
  minDate,
  maxDate,
  selectedDate,
  onDateChange,
  disabled = false,
  hasError = false,
  monthRef,
  yearRef,
  dayRef,
  labels = {},
  order = 'month/day/year',
  reverseYears,
  hideLabels,
  white,
  ...args
}: ISelectDatepickerCustom) {
  const [year, setYear] = useState(-1)
  const [month, setMonth] = useState(-1)
  const [day, setDay] = useState(-1)
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const orderArray = useMemo(
    () => (order ? order.split('/') : ['month', 'day', 'year']),
    [order],
  )

  const yearOptions = useMemo(
    () => getYearsObject(minDate, maxDate, reverseYears),
    [maxDate, reverseYears, minDate],
  )
  const monthOptions = useMemo(
    () => getMonthsObject(minDate, maxDate, year, labels.months),
    [maxDate, labels.months, minDate, year],
  )
  const dayOptions = useMemo(
    () => getDaysObject(minDate, maxDate, month + 1, year),
    [maxDate, month, minDate, year],
  )

  const handleYearChange = useCallback(
    (e: Option | null) => {
      setYear(e?.value || -1)

      // Validate if current month is in new month options
      const mOptions = getMonthsObject(
        minDate,
        maxDate,
        e?.value,
      )
      if (!mOptions.some((val) => val.value === month + 1)) {
        setMonth(-1)
      }

      // Validate if current day is in new day options
      const dOptions = getDaysObject(
        minDate,
        maxDate,
        month + 1,
        e?.value,
      )
      if (!dOptions.some((val) => val.value === day)) {
        setDay(-1)
      }
    },
    [day, month, minDate, maxDate],
  )

  const handleMonthChange = useCallback(
    (e: Option | null) => {
      const monthIndex = Number.parseInt(e?.value.toString() ?? '-1', 10) - 1
      setMonth(monthIndex)

      // Validate if current day is in new day options
      const dOptions = getDaysObject(
        minDate,
        maxDate,
        e?.value,
        year,
      )
      if (!dOptions.some((val) => val.value === day)) {
        setDay(-1)
      }
    },
    [day, year, minDate, maxDate],
  )

  const handleDayChange = useCallback(
    (e: Option | null) => {
      setDay(e?.value || -1)
    },
    [],
  )

  const field: { day: JSX.Element; month: JSX.Element; year: JSX.Element } = useMemo(() => {
    const elements = {
      day: (
        <SelectRenderer
          id="day"
          labels={{
            hide: hideLabels,
            main: labels.dayLabel || 'Day',
            placeholder: labels.dayPlaceholder || 'Select Day',
          }}
          value={day !== -1 ? { label: day.toString(), value: day } : null}
          disabled={disabled}
          onChangeHandler={handleDayChange}
          selectOptions={dayOptions}
          white={white}
        />
      ),
      month: (
        <SelectRenderer
          id="month"
          labels={{
            hide: hideLabels,
            main: labels.monthLabel || 'Month',
            placeholder: labels.monthPlaceholder || 'Select Month',
          }}
          value={month !== -1 ? { label: monthNames[month], value: month } : null}
          disabled={disabled}
          onChangeHandler={handleMonthChange}
          selectOptions={monthOptions}
          white={white}
        />
      ),
      year: (
        <SelectRenderer
          id="year"
          labels={{
            hide: hideLabels,
            main: labels.yearLabel || 'Year',
            placeholder: labels.yearPlaceholder || 'Select Year',
          }}
          value={year !== -1 ? { label: year.toString(), value: year } : null}
          disabled={disabled}
          onChangeHandler={handleYearChange}
          selectOptions={yearOptions}
          white={white}
        />
      ),
    }

    return elements;
  }, [
    day,
    dayOptions,
    dayRef,
    disabled,
    handleDayChange,
    handleMonthChange,
    handleYearChange,
    hideLabels,
    labels.dayLabel,
    labels.dayPlaceholder,
    labels.monthLabel,
    labels.monthPlaceholder,
    labels.yearLabel,
    labels.yearPlaceholder,
    month,
    monthOptions,
    monthRef,
    year,
    yearOptions,
    yearRef,
    white,
  ])

  useEffect(() => {
    if (selectedDate !== undefined && selectedDate !== null) {
      setDay(Number(selectedDate.getUTCDate()))
      setMonth(Number(selectedDate.getUTCMonth()))
      setYear(Number(selectedDate.getUTCFullYear()))
    }
  }, [selectedDate])

  useEffect(() => {
    if (year !== -1 && month !== -1 && day !== -1) {
      const date = Date.UTC(year, month, day)

      onDateChange(new Date(date))
    } else {
      onDateChange(null)
    }
  }, [day, month, year])

  return (
    <div
      {...args}
      style={{ display: 'flex' }}
      id={id}
      className={clsx(styles.container, className, white && styles.containerWhite)}
      {...(hasError ? { 'aria-invalid': true } : null)}
    >
      {orderArray.map((key) => (
        <React.Fragment key={`${key}`}>
          {field[key as 'day' | 'month' | 'year']}
        </React.Fragment>
      ))}
    </div>
  )
}

export default SelectDatepicker
